import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getMicroSitesDomains, updateMicroSitesDomains} from 'src/openapi/admin/apis/MicroSitesDomainsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {MicroSiteDomains} from 'src/openapi/admin/models/MicroSiteDomains';

type Props = {
    accountId: number,
};

export const useApprovedDomains = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {validationErrors} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getMicroSitesDomains({accountId}, {signal}));
        if (response.payload.success) {
            const payload: {success: true, error: false, data: MicroSiteDomains} = {
                success: true,
                error: false,
                data: {
                    ...response.payload.data,
                    domains: response.payload.data.domains.length ? response.payload.data.domains : [''],
                },
            };
            return Promise.resolve({type: '', payload});
        } else if (response.payload.data.message === 'MicroSite domain is disabled') {
            const payload: {success: true, error: false, data: MicroSiteDomains} = {
                success: true,
                error: false,
                data: {
                    disabled: true,
                    domains: [''],
                },
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [accountId, apiDispatch]);

    const saveDispatchAction = useCallback((
        microSiteDomains: MicroSiteDomains,
        signal: AbortSignal,
    ) => apiDispatch(
        updateMicroSitesDomains({accountId, microSiteDomains: {
            ...microSiteDomains,
            domains: isDeepEqual({prev: microSiteDomains.domains, current: ['']})
                ? []
                : microSiteDomains.domains,
        }}, {signal}),
    ), [accountId, apiDispatch]);

    const saveValidation = useCallback(() => !validationErrors.length, [validationErrors]);

    return useCommonDataHook({
        initValue: {
            domains: [''],
            disabled: true,
        },
        getDispatchAction,
        saveDispatchAction,
        saveValidation,
    });
};
